import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './CVPage.module.css';

export default function CVPage({ data }) {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('pdf'); // 'both' | 'interactive' | 'pdf'

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const driveLink = data?.profile?.drive_cv_link?.trim() || 'https://drive.google.com/file/d/1cUQ0xL9TifBduYIkaSI6Zl41XEw7G_9v/view?usp=sharing';
  const pdfSrc = driveLink.replace(/\/view.*/, '/preview');
  const downloadLink = driveLink;

  return (
    <div className={styles.container}>
      {/* ── CV HEADER & ACTION BAR ── */}
      <header className={styles.cvHeaderCard}>
        <div className={styles.cvHeaderTop}>
          <div className={styles.cvIdentity}>
            <span className={styles.cvEyebrow}>CURRICULUM VITAE · ACADEMIC DOSSIER</span>
            <h1 className={styles.cvName}>Jolok Banarjee</h1>
            <p className={styles.cvRole}>
              B.Sc. in Aerospace Engineering · Research Assistant (AI for Predictive Maintenance & Autonomous UAVs)
            </p>
            <div className={styles.cvContactBar}>
              <a href="mailto:jolok21014026@aaub.edu.bd">✉️ jolok21014026@aaub.edu.bd</a>
              
              <span>📍 Sylhet / Dhaka, Bangladesh</span>
            </div>
          </div>

          <div className={styles.cvActionGroup}>
            <a
              href={downloadLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.downloadBtn}
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Full CV (PDF) ↗
            </a>
            <button
              type="button"
              onClick={() => navigate('/research')}
              className={styles.secondaryActionBtn}
            >
              📚 View Publications →
            </button>
          </div>
        </div>

        {/* Highlights Strip */}
        <div className={styles.highlightsBar}>
          <div className={styles.highlightStat}>
            <span className={styles.highlightVal}>3.56 / 4.00</span>
            <span className={styles.highlightLbl}>CGPA (Last Sem: 3.88)</span>
          </div>
          <div className={styles.highlightStat}>
            <span className={styles.highlightVal}>4 Papers</span>
            <span className={styles.highlightLbl}>IEEE & AIAA (2 Published)</span>
          </div>
          <div className={styles.highlightStat}>
            <span className={styles.highlightVal}>Patent #1007044</span>
            <span className={styles.highlightLbl}>Bangla Voice UAV (Mar 2026)</span>
          </div>
         
          <div className={styles.highlightStat}>
            <span className={styles.highlightVal}>Best Thesis</span>
            <span className={styles.highlightLbl}>AAUB Award 2025</span>
          </div>
        </div>

        {/* View Mode Switcher */}
        <div className={styles.viewToggleBar} role="tablist" aria-label="CV display mode">
          <span className={styles.viewToggleLabel}>Display Mode:</span>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'both'}
            className={`${styles.viewToggleBtn} ${viewMode === 'both' ? styles.viewToggleActive : ''}`}
            onClick={() => setViewMode('both')}
          >
            📑 Interactive Summary + PDF Viewer
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'interactive'}
            className={`${styles.viewToggleBtn} ${viewMode === 'interactive' ? styles.viewToggleActive : ''}`}
            onClick={() => setViewMode('interactive')}
          >
            ⚡ Interactive Web CV Only
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'pdf'}
            className={`${styles.viewToggleBtn} ${viewMode === 'pdf' ? styles.viewToggleActive : ''}`}
            onClick={() => setViewMode('pdf')}
          >
            📄 Embedded PDF Document Only
          </button>
        </div>
      </header>

      {/* ── INTERACTIVE STRUCTURED ACADEMIC CV ── */}
      {(viewMode === 'both' || viewMode === 'interactive') && (
        <div className={styles.interactiveCvGrid}>
          {/* 1. Education & Thesis */}
          <section className={styles.cvSectionCard}>
            <div className={styles.cvSectionHeader}>
              <h2 className={styles.cvSectionTitle}>🎓 Education & Undergraduate Thesis</h2>
              <button type="button" onClick={() => navigate('/education')} className={styles.sectionLinkBtn}>
                Full Education Page →
              </button>
            </div>
            <div className={styles.cvEntry}>
              <div className={styles.cvEntryHeader}>
                <div>
                  <h3 className={styles.cvEntryTitle}>B.Sc. in Aerospace Engineering</h3>
                  <p className={styles.cvEntryOrg}>Aviation and Aerospace University, Bangladesh (AAUB)</p>
                </div>
                <span className={styles.cvEntryDate}>Jan 2022 – Mar 2026</span>
              </div>
              <div className={styles.cvPillsRow}>
                <span className={styles.cvPillAccent}>CGPA: 3.56 / 4.00</span>
                <span className={styles.cvPillAccent}>Final Semester GPA: 3.88 / 4.00</span>
                <span className={styles.cvPillGold}>🏆 Best Thesis Award 2025</span>
              </div>
              <p className={styles.cvEntryText}>
                <strong>Undergraduate Thesis:</strong> <em>"A Novel Integrated ML Framework with Comparative Evaluation for Fault Detection, Condition Monitoring, and RUL Prediction of Turbofan Engines"</em> — Supervised by <strong>Air Cdre Dr. Saifur Rahman Bakaul</strong> (Chairman, Dept. of Aerospace Engineering). Achieved 98.5% condition monitoring accuracy (Bi-LSTM) and 13.54 RUL RMSE (LSTM) on NASA C-MAPSS FD001.
              </p>
            </div>
          </section>

          {/* 2. Research & Professional Experience (Ordered: Research Experience First, then Research Mentor) */}
          <section className={styles.cvSectionCard}>
            <div className={styles.cvSectionHeader}>
              <h2 className={styles.cvSectionTitle}>🔬 Research, Mentorship & Industrial Experience</h2>
              <button type="button" onClick={() => navigate('/experience')} className={styles.sectionLinkBtn}>
                Full Experience Page →
              </button>
            </div>

            <div className={styles.cvTimelineList}>
              <div className={styles.cvEntry}>
                <div className={styles.cvEntryHeader}>
                  <div>
                    <span className={styles.cvRoleTagTeal}>Research Experience</span>
                    <h3 className={styles.cvEntryTitle}>Research Assistant — AI Toolkit for Predictive Maintenance of Aircraft Systems</h3>
                    <p className={styles.cvEntryOrg}>Aviation and Aerospace University, Bangladesh (AAUB) · PI: Air Cdre Dr. Saifur Rahman Bakaul</p>
                  </div>
                  <span className={styles.cvEntryDate}>Feb 2026 – Present</span>
                </div>
                <ul className={styles.cvBulletList}>
                  <li>Contribute to the funded research project developing an AI/ML toolkit for aircraft system predictive maintenance and prognostics.</li>
                  <li>Lead sensor data preprocessing, deep learning model training, and validation workflows for deployable aviation health monitoring tools.</li>
                </ul>
              </div>

              <div className={styles.cvEntry}>
                <div className={styles.cvEntryHeader}>
                  <div>
                    <span className={styles.cvRoleTagBlue}>Research Mentor</span>
                    <h3 className={styles.cvEntryTitle}>Research Mentor (Research Supervisor)</h3>
                    <p className={styles.cvEntryOrg}>ResearchBuddy AI — Intelligent System Engineering Lab</p>
                  </div>
                  <span className={styles.cvEntryDate}>Jun 2026 – Present</span>
                </div>
                <ul className={styles.cvBulletList}>
                  <li>Mentor 10–15 undergraduate students at the beginning of their academic research journey, providing structured guidance on problem formulation, literature review, and methodology design.</li>
                </ul>
              </div>

              <div className={styles.cvEntry}>
                <div className={styles.cvEntryHeader}>
                  <div>
                    <span className={styles.cvRoleTagEmerald}>Industrial MRO Internship</span>
                    <h3 className={styles.cvEntryTitle}>Industrial Intern — Maintenance Operations</h3>
                    <p className={styles.cvEntryOrg}>Biman Bangladesh Airlines · Hazrat Shahjalal International Airport, Dhaka</p>
                  </div>
                  <span className={styles.cvEntryDate}>Jun 2024 – Jul 2024</span>
                </div>
                <ul className={styles.cvBulletList}>
                  <li>Assisted with 40+ maintenance and inspection operations across Boeing 787, 777, 737, and Dash 8 fleets, bridging theoretical prognostics research with hangar-floor MRO procedures.</li>
                </ul>
              </div>

              <div className={styles.cvEntry}>
                <div className={styles.cvEntryHeader}>
                  <div>
                    <span className={styles.cvRoleTagPurple}>Research & Technical Leadership</span>
                    <h3 className={styles.cvEntryTitle}>Founder & Director — Frontier Alliance · Team Manager — AIAA Design Team · VP — Drone Club (AAUB)</h3>
                    <p className={styles.cvEntryOrg}>Aviation and Aerospace University, Bangladesh (AAUB)</p>
                  </div>
                  <span className={styles.cvEntryDate}>Jun 2023 – Present</span>
                </div>
                <ul className={styles.cvBulletList}>
                  <li>Founded Frontier Alliance and led 10+ member cross-functional teams in AIAA Aircraft Design Competitions (winning 2nd & 3rd Place in AIAA Region VII 2025) and organized Impulse 1.0 (700+ participants).</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 3. Publications & Patent */}
          <section className={styles.cvSectionCard}>
            <div className={styles.cvSectionHeader}>
              <h2 className={styles.cvSectionTitle}>📚 Publications, Manuscripts & Patent</h2>
              <button type="button" onClick={() => navigate('/research')} className={styles.sectionLinkBtn}>
                Read Case Studies →
              </button>
            </div>
            <div className={styles.cvCompactList}>
              <div className={styles.cvCompactItem}>
                <span className={styles.statusBadgePub}>Published · IEEE</span>
                <p>
                  M. H. Ishak, <strong>J. Banarjee</strong>, A. S. Oishy, and S. R. Bakaul, <em>"A Novel AI-Driven Anomaly Detection Framework for Predictive Maintenance of Aircraft Engines,"</em> 2025 11th Int. Conf. on Mechanical and Aerospace Engineering (MEAE), Beijing, China (IEEE / Scopus). <strong>Best Presentation Award</strong>. DOI: <a href="https://doi.org/10.1109/MEAE68077.2025.11557878" target="_blank" rel="noreferrer">10.1109/MEAE68077.2025.11557878</a>
                </p>
              </div>
              <div className={styles.cvCompactItem}>
                <span className={styles.statusBadgePub}>Published · AIAA</span>
                <p>
                  M. U. Ahmaad, A. Sadi, <strong>J. Banarjee</strong>, M. A. Israq, and S. R. Bakaul, <em>"Static Structural Analysis And Material Selection of A Supersonic Fighter Aircraft's Wing,"</em> AIAA Region VII Student Conference 2025 (<strong>3rd Place Winner, Team Category</strong>). DOI: <a href="https://doi.org/10.2514/6.2025-106584" target="_blank" rel="noreferrer">10.2514/6.2025-106584</a>
                </p>
              </div>
              <div className={styles.cvCompactItem}>
                <span className={styles.statusBadgeRev}>Under Review</span>
                <p>
                  <strong>J. Banarjee</strong>, M. H. Ishak, and S. R. Bakaul, <em>"End-to-End Predictive Maintenance Framework for Turbofans: Fault Detection, Condition Monitoring, and Prognostics,"</em> submitted to <em>IEEE Aerospace & Electronic Systems Magazine / Elsevier Aerospace Science & Technology</em> (2026).
                </p>
              </div>
              <div className={styles.cvCompactItem}>
                <span className={styles.statusBadgeRev}>Under Review</span>
                <p>
                  <strong>J. Banarjee</strong>, H. P. Himel, T. W. Opul, T. A. Anny, and S. Khan, <em>"Sustainable Additive Manufacturing using Recycled PET: Extrusion Design, Mechanical Testing, and Data-Driven Prediction,"</em> submitted to <em>Elsevier Sustainable Production and Consumption</em> (<strong>2nd Place Winner, AIAA Student Conference VII, 2025</strong>).
                </p>
              </div>
              <div className={styles.cvCompactItem}>
                <span className={styles.statusBadgePat}>Granted Patent</span>
                <p>
                  <strong>Patent No. 1007044 (Issued Mar 2026):</strong> <em>"Voice-Operated Remotely Piloted Aircraft (Bangla Command)"</em> — Proprietary integration of Bangla ASR, MAVLink telemetry, and Smart Virtual Assistant for autonomous UAV operations.
                </p>
              </div>
            </div>
          </section>

          {/* 4. Two-Column Honors + Skills Summary */}
          <div className={styles.cvTwoCol}>
            <section className={styles.cvSectionCard}>
              <div className={styles.cvSectionHeader}>
                <h2 className={styles.cvSectionTitle}>🏆 Key Honors & Grants</h2>
                <button type="button" onClick={() => navigate('/about')} className={styles.sectionLinkBtn}>
                  View Certificates →
                </button>
              </div>
              <ul className={styles.cvHonorList}>
                <li><strong>Best Thesis Award 2025</strong> — Dept. of Aerospace Engineering, AAUB</li>
                <li><strong>Best Aerospace Vehicle Design Award 2025</strong> — Sky Sentinel S26 Interceptor, AAUB</li>
                <li><strong>BDT 55 Lakh Research Grant (2024)</strong> — World Bank RIC Funding for Medical Delivery UAV</li>
                <li><strong>2nd Place & 3rd Place (Team Category)</strong> — AIAA Region VII Student Conference 2025 (Australia)</li>
                <li><strong>1st Runner-Up</strong> — NASA Space Apps Challenge Bangladesh 2023 (Rangpur)</li>
                <li><strong>2nd Place (Bangladesh Region)</strong> — 4th JAXA Kibo Robot Programming Challenge 2023</li>
              </ul>
            </section>

            <section className={styles.cvSectionCard}>
              <div className={styles.cvSectionHeader}>
                <h2 className={styles.cvSectionTitle}>🛠️ Core Technical Stack</h2>
                <button type="button" onClick={() => navigate('/certifications')} className={styles.sectionLinkBtn}>
                  5 Certifications →
                </button>
              </div>
              <div className={styles.cvSkillGroups}>
                <div>
                  <span className={styles.cvSkillGroupLabel}>AI, Prognostics & Data Science</span>
                  <p className={styles.cvSkillGroupItems}>Python, TensorFlow, Keras, PyTorch, Scikit-learn, XGBoost, SHAP, Optuna, Pandas, NumPy, SPSS</p>
                </div>
                <div>
                  <span className={styles.cvSkillGroupLabel}>Aerospace, CAD & Simulation</span>
                  <p className={styles.cvSkillGroupItems}>ANSYS Mechanical (FEA), ANSYS Fluent (CFD), SolidWorks 2024, MATLAB & Simulink</p>
                </div>
                <div>
                  <span className={styles.cvSkillGroupLabel}>Autonomous UAVs & Embedded IoT</span>
                  <p className={styles.cvSkillGroupItems}>ArduPilot, MAVLink, MAVProxy, Raspberry Pi, Arduino, STM32, PID Control, AWS S3</p>
                </div>
                <div>
                  <span className={styles.cvSkillGroupLabel}>Full-Stack Web & Documentation</span>
                  <p className={styles.cvSkillGroupItems}>React.js, FastAPI, Streamlit, JavaScript (ES6+), HTML5, CSS3, LaTeX, Git/GitHub</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}

      {/* ── EMBEDDED OFFICIAL PDF VIEWER ── */}
      {(viewMode === 'both' || viewMode === 'pdf') && (
        <section className={styles.pdfViewerSection}>
          <div className={styles.pdfViewerHeader}>
            <div>
              <h2 className={styles.pdfViewerTitle}>📄 Official 3-Page Curriculum Vitae (PDF Document)</h2>
              <p className={styles.pdfViewerSub}>
                Embedded preview below. If your browser blocks third-party Google Drive embeds, use the direct download button.
              </p>
            </div>
            <a
              href={downloadLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.openExternalBtn}
            >
              Open PDF in New Tab ↗
            </a>
          </div>
          <div className={styles.cvWrapper}>
            <iframe
              src={pdfSrc}
              title="Jolok Banarjee Official CV PDF"
              className={styles.iframe}
              loading="lazy"
            />
          </div>
        </section>
      )}

      {/* ── PHD OPPORTUNITIES CTA ── */}
      <section className={styles.phdCtaSection}>
        <div className={styles.phdCtaCard}>
          <h3 className={styles.phdCtaTitle}>Seeking Fully Funded PhD Opportunities</h3>
          <p className={styles.phdCtaText}>
            I am actively looking for PhD positions in{' '}
            <strong>Intelligent Aerospace Systems</strong>,{' '}
            <strong>Structural Health Monitoring</strong>, and{' '}
            <strong>Autonomous UAV Systems</strong>. If your research group aligns with my
            publication record and research vision, I would welcome the opportunity to discuss
            potential collaboration and graduate funding opportunities.
          </p>
          <div className={styles.phdCtaButtons}>
            <a href="mailto:jolok21014026@aaub.edu.bd" className={styles.phdBtnPrimary}>
              ✉️ Email Me (jolok21014026@aaub.edu.bd)
            </a>
            <button
              type="button"
              onClick={() => navigate('/research')}
              className={styles.phdBtnSecondary}
            >
              Explore Research Publications →
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}